import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Filing } from "../models/Filing";
import { FilingSection } from "../models/FilingSection";
import { Comment } from "../models/Comment";
import { FilingService } from "../services/FilingService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { useSocket } from "./SocketContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { select } from "slate";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface FilingContextProps {
  loading: Loadings;
  error: Errors;
  filing: Filing | null;
  setFiling: (filing: Filing | null) => void;
  filings: Filing[];
  comments: Comment[];
  createFiling: (
    file: globalThis.File | null,
    filingData: Partial<
      Omit<
        Filing,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "sections"
      >
    >,
  ) => Promise<string>;
  updateFiling: (
    filingId: string,
    filingData: Partial<Omit<Filing, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteFilingById: (filingId: string) => Promise<void>;
  setSelectedFiling: (filingId: string) => void;

  // Section
  deleteSection: (filingId: string, sectionId: string) => Promise<void>;

  // Comment
  deleteComment: (
    filingId: string,
    sectionId: string,
    commentId: string,
  ) => Promise<void>;
}

const FilingContext = createContext<FilingContextProps | undefined>(undefined);

export const useFiling = (): FilingContextProps => {
  const context = useContext(FilingContext);
  if (!context) {
    throw new Error("useFiling must be used within a FilingProvider");
  }
  return context;
};

export const FilingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [filings, setFilings] = useState<Filing[]>([]);
  const [filing, setFiling] = useState<Filing | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const fetchFilings = async () => {
    if (!user || !currentMatterId) return;
    try {
      const docs = await MatterService.getFilings(currentMatterId);
      setFilings(docs);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchFilings();
  }, [user, currentMatterId]);

  useSocketEventByIds(
    "filings:changed",
    ["*"],
    async (updatedDocument: Filing) => {
      if (updatedDocument._id === filing?._id) {
        const updatedFiling = await FilingService.getFiling(
          updatedDocument._id,
        );
        setFiling(updatedFiling);
      } else {
        fetchFilings();
      }
    },
  );

  useSocketEventByIds(
    "filingsections:changed",
    ["*"],
    async (updatedDocument: FilingSection) => {
      if (updatedDocument.filing === filing?._id) {
        const updatedFiling = await FilingService.getFiling(
          updatedDocument.filing,
        );
        setFiling(updatedFiling);
      }
      fetchFilings();
    },
  );

  const setSelectedFiling = async (filingId: string) => {
    setLoading((prev) => ({ ...prev, details: true }));
    const selected = await FilingService.getFiling(filingId);
    setFiling(selected);
    setLoading((prev) => ({ ...prev, details: false }));
    setError((prev) => ({ ...prev, details: null }));
  };

  const createFiling = async (
    file: globalThis.File | null,
    filingData: Partial<
      Omit<
        Filing,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "sections"
      >
    >,
  ): Promise<string> => {
    const newId = await FilingService.createFiling(file, {
      ...filingData,
      matter: currentMatterId!,
    });
    return newId;
  };

  const updateFiling = async (
    filingId: string,
    updatedData: Partial<Omit<Filing, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await FilingService.updateFiling(filingId, {
      ...updatedData,
      updatedBy: user!.uid,
      matter: currentMatterId!,
    });
  };

  const deleteFilingById = async (filingId: string) => {
    if (!currentMatterId) return;
    await FilingService.deleteFiling(filingId);
    fetchFilings();
  };

  const deleteSection = async (filingId: string, sectionId: string) => {
    try {
      await FilingService.deleteSection(filingId, sectionId);
      setFiling((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          sections: prev.sections.filter((s) => s._id !== sectionId),
        };
      });
    } catch (err) {
      console.error("Failed to delete section:", err);
    }
  };

  // Comment
  const deleteComment = async (
    filingId: string,
    sectionId: string,
    commentId: string,
  ) => {
    try {
      await FilingService.deleteComment(filingId, sectionId, commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  return (
    <FilingContext.Provider
      value={{
        loading,
        error,
        filing,
        setFiling,
        filings,
        comments,
        createFiling,
        updateFiling,
        deleteFilingById,
        setSelectedFiling,
        // Section
        deleteSection,
        // Comment
        deleteComment,
      }}
    >
      {children}
    </FilingContext.Provider>
  );
};
